import { useState } from "react";
import { useAuthContext } from "./useAuthContext";
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAccountContext } from "./useAccountContext";
import { useSettingsContext } from "./useSettingsContext";

const useLeaveTypes = () => {
    const { t } = useTranslation();
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const { username, user, clientApi, dispatch: authDispatch } = useAuthContext();
    const { account } = useAccountContext();
    const { isWinAuth } = useSettingsContext();

    const axiosInstance = axios.create({
        baseURL: `${clientApi}`,
        headers: {
            'accepts': 'application/json',
            'Content-Type': 'application/json',
        },
    });

    axiosInstance.interceptors.request.use(request => {
        const accessToken = user.token;
        if (accessToken) {
            request.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        return request;
    }, error => {
        return Promise.reject(error);
    });

    axiosInstance.interceptors.response.use(
        response => response,
        async error => {
            const originalRequest = error.config;
            if (error.response.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;
                try {
                    if (!isWinAuth) {
                        // Make a request to your auth server to refresh the token.
                        const response = await axiosInstance.post(`${clientApi}api/auth/refresh`, user);
                        // Store the new access and refresh tokens.
                        authDispatch({ type: 'Refresh', payload: response.data, clientApi: clientApi, username: username });

                        // Update the authorization header with the new access token.
                        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                    }
                    else {
                        const newToken = await acquireSilentToken();
                        let tmpUser = user;
                        tmpUser.token = newToken;
                        authDispatch({ type: 'REFRESH', payload: tmpUser, clientApi: clientApi, username: username });
                        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                    }
                    return axios(originalRequest);
                } catch (refreshError) {
                    console.error('Token refresh failed:', refreshError);
                    authDispatch({ type: 'LOGOUT' });
                    return Promise.reject(refreshError);
                }
            }
            return Promise.reject(error);
        }
    );

    const GetLeaveTypes = async (company) => {
        setIsLoading(true);
        let resp;
        await axiosInstance.get(`${clientApi}api/Company/LeaveTypes`, {
            params: {
                company: company
            }
        })
            .then((response) => {
                resp = response.data;
            })
            .catch((error) => {
                setError(error);
            });
        setIsLoading(false);
        return resp;
    };

    const SaveLeaveType = async (leaveType, company) => {
        setIsLoading(true);
        try {
            const formData = new FormData();
            let leaveTypeArray = []
            const keys = Object.keys(leaveType);
            keys.forEach(key => {
                const nestedObject = leaveType[key];
                leaveTypeArray.push(nestedObject)
            });
            let saveLeave = leaveTypeArray
            formData.append('leaveType', JSON.stringify({ saveLeave }));
            const response = await axiosInstance.post(`${clientApi}api/Company/SaveLeaveTypes`, formData, {
                params: {
                    company: company,
                    userid: account.employeeId,
                },
                headers: { 'Content-Type': 'multipart/form-data' }

            });
            setIsLoading(false);
            if (response.status === 200)
                return true;
            else
                return false;
        } catch (error) {
            setError(error);
            setIsLoading(false);
            throw error;
        }
    };

    const DeleteLeaveType = async (rowguid) => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.delete(`${clientApi}api/Company/DeleteLeaveType`, {
                data: rowguid
            });
            setIsLoading(false);
            if (response.status === 200)
                return true;
            else
                return false;
        } catch (error) {
            setError(error);
            console.log(error.response.data)
            setIsLoading(false);
            // * This is called when leavetypes cannot be deleted due to DB constrains - i.e. Used in leave schemes or leaves are already applied 
            throw error.response.data;
        }
    };

    return { isLoading, error, GetLeaveTypes, SaveLeaveType, DeleteLeaveType };
};

export default useLeaveTypes;